import { useCallback, useEffect, useMemo } from "react";

type UsePruneSelectedValuesArgs<TValue> = {
    selectedValues?: TValue[] | null;
    setSelectedValues: (values: TValue[]) => void;
    isValueAllowed: (value: TValue) => boolean;
    enabled?: boolean;
};

type UsePruneSelectedValuesByOptionsArgs<TSelected, TOption> = {
    selectedValues?: TSelected[] | null;
    setSelectedValues: (values: TSelected[]) => void;
    availableOptions?: TOption[] | null;
    getSelectedValue: (selected: TSelected) => unknown;
    getOptionValue: (option: TOption) => unknown;
    enabled?: boolean;
};

const EMPTY_VALUES: never[] = [];
const normalizeValue = (value: unknown) => `${value ?? ""}`;

export function usePruneSelectedValues<TValue>({
    selectedValues,
    setSelectedValues,
    isValueAllowed,
    enabled = true,
}: UsePruneSelectedValuesArgs<TValue>) {
    useEffect(() => {
        if (!enabled) return;

        const currentValues = selectedValues ?? (EMPTY_VALUES as TValue[]);
        const nextValues = currentValues.filter(isValueAllowed);

        if (nextValues.length === currentValues.length) return;
        setSelectedValues(nextValues);
    }, [enabled, isValueAllowed, selectedValues, setSelectedValues]);
}

export function usePruneSelectedValuesByOptions<TSelected, TOption>({
    selectedValues,
    setSelectedValues,
    availableOptions,
    getSelectedValue,
    getOptionValue,
    enabled = true,
}: UsePruneSelectedValuesByOptionsArgs<TSelected, TOption>) {
    const availableValueSet = useMemo(
        () =>
            new Set(
                (availableOptions ?? EMPTY_VALUES).map((option) =>
                    normalizeValue(getOptionValue(option)),
                ),
            ),
        [availableOptions, getOptionValue],
    );

    const isValueAllowed = useCallback(
        (value: TSelected) => {
            const selectedValue = normalizeValue(getSelectedValue(value));
            if (!selectedValue) return false;
            return availableValueSet.has(selectedValue);
        },
        [availableValueSet, getSelectedValue],
    );

    usePruneSelectedValues({
        selectedValues,
        setSelectedValues,
        isValueAllowed,
        enabled,
    });
}
